import { useEffect, useState } from "react";
import API from "../lib/api";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const ServicesPicker = ({ value = [], onChange, testIdPrefix = "services", allowAdd = true, endpoint = "/suppliers/service-options" }) => {
  const [options, setOptions] = useState([]);
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const [newService, setNewService] = useState("");

  useEffect(() => {
    API.get(endpoint).then(({ data }) => setOptions(data.services || [])).catch(() => {});
  }, [endpoint]);

  const selected = new Set(value);
  const toggle = (svc) => {
    const next = new Set(selected);
    if (next.has(svc)) next.delete(svc); else next.add(svc);
    onChange([...next]);
  };

  const handleAdd = async () => {
    const name = newService.trim();
    if (!name) return;
    try {
      const { data } = await API.post("/suppliers/service-options", { name });
      setOptions((prev) => (prev.some((o) => o.toLowerCase() === data.name.toLowerCase()) ? prev : [...prev, data.name].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))));
      if (!selected.has(data.name)) toggle(data.name);
      setNewService("");
      setAdding(false);
      toast.success(data.message === "Already listed" ? `"${data.name}" is already listed — selected it` : `"${data.name}" added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add service");
    }
  };

  const visible = options.filter((o) => o.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div className="border border-border rounded-md" data-testid={`${testIdPrefix}-picker`}>
      <div className="p-2 border-b border-border flex gap-2">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Type to find a service..." className="h-8 text-xs" data-testid={`${testIdPrefix}-picker-filter`} />
        {allowAdd && (
          <Button type="button" variant="outline" size="sm" className="h-8 px-2 shrink-0 text-xs" onClick={() => setAdding((a) => !a)} data-testid={`${testIdPrefix}-add-toggle`}>
            <Plus size={13} className="mr-1" /> Add
          </Button>
        )}
      </div>
      {adding && allowAdd && (
        <div className="p-2 border-b border-border flex gap-2 bg-muted/30">
          <Input
            value={newService}
            onChange={(e) => setNewService(e.target.value)}
            placeholder="New service name..."
            className="h-8 text-xs"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
            data-testid={`${testIdPrefix}-add-input`}
          />
          <Button type="button" size="sm" className="h-8 px-3 shrink-0 text-xs" onClick={handleAdd} data-testid={`${testIdPrefix}-add-confirm`}>Save</Button>
        </div>
      )}
      <div className="max-h-44 overflow-y-auto p-2">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1">
          {visible.map((svc) => (
            <label key={svc} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={selected.has(svc)} onCheckedChange={() => toggle(svc)} data-testid={`${testIdPrefix}-option-${slug(svc)}`} />
              <span className="truncate">{svc}</span>
            </label>
          ))}
        </div>
        {visible.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">{allowAdd ? 'No match — use "Add" to create it' : "No match"}</p>}
      </div>
      {value.length > 0 && (
        <div className="px-2 py-1.5 border-t border-border flex flex-wrap gap-1 items-center" data-testid={`${testIdPrefix}-picker-selected`}>
          <span className="text-[10px] text-muted-foreground mr-1">{value.length} selected — click to remove:</span>
          {value.map((s) => (
            <Badge key={s} variant="secondary" className="text-[9px] cursor-pointer" onClick={() => toggle(s)} data-testid={`${testIdPrefix}-selected-${slug(s)}`}>
              {s} ✕
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
